'use client';

import { API_ROUTES } from '@shared/api-utils';
import { Button, FormControl, FormField, FormItem } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { NumericCode } from '@shared/icons/system';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMultistepForm } from '../../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { OtpInput } from '../../../../../../form-controls/OtpInput/OtpInput';
import { formatPhoneNumberForDisplay } from '../../../../../../form-controls/PhoneNumberInput/phoneNumberUtils';
import { ContinueButton } from '../../../../../../Forms/Registration/components/ContinueButton/ContinueButton';
import { toast } from '../../../../../../Toast/Toast';
import { useVerificationApiMutation } from '../../../../hooks/useVerificationApiMutation';
import { invalidateMyAccountVerificationQueries } from '../../../../utils/invalidateMyAccountVerificationQueries';
import { getMobileOtpResendLimiterStorageKey } from '../../../../utils/otpResendLimiterStorageKey';
import { isVerificationRateLimitError, resolveVerificationMutationFailure } from '../../../../utils/verificationErrorUtils';
import type { ResendVerificationResponse } from '../../../edit-email/types';
import { SettingsVerificationTechnicalFailure } from '../../../SettingsVerificationTechnicalFailure';
import type { EditMobileNumberFormData, ResendVerificationPayload, StepProps, VerifyVerificationPayload, VerifyVerificationResponse } from '../../types/edit-mobile-number';

const MOBILE_VERIFICATION_CODE_PATTERN = /^\d{6}$/;

const VerificationStep = ({ onRegisterBackHandler, namespace, onVerificationFailureDone }: StepProps) => {
  const queryClient = useQueryClient();
  const { control, formState, trigger, getValues, setError } = useFormContext<EditMobileNumberFormData>();
  const verificationCode = useWatch({ control, name: 'verificationCode' }) ?? '';
  const isVerificationCodeComplete = MOBILE_VERIFICATION_CODE_PATTERN.test(verificationCode);
  const { updateFormData, goToNextStep, goToPreviousStep } = useMultistepForm();
  const mobileNumber = getValues('mobileNumber');
  const prefix = mobileNumber?.prefix || '';
  const number = mobileNumber?.number || '';
  const MOBILE_NUMBER = formatPhoneNumberForDisplay(prefix, number);
  const otpResendStorageKey = getMobileOtpResendLimiterStorageKey(prefix, number);
  const t = useTranslations(namespace);
  const tTechnicalFailure = useTranslations('my_account.settings.verification_technical_failure');
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
  const [showTechnicalFailure, setShowTechnicalFailure] = useState(false);
  const isVerifyingRef = useRef(false);

  const { mutateAsync: verifyCode, isPending: isVerifying } = useVerificationApiMutation<VerifyVerificationResponse, VerifyVerificationPayload>(
    API_ROUTES.MY_ACCOUNT.VERIFICATION.VERIFY
  );

  const { mutateAsync: resendCode } = useVerificationApiMutation<ResendVerificationResponse, ResendVerificationPayload>(API_ROUTES.MY_ACCOUNT.VERIFICATION.RESEND);

  useEffect(() => {
    const backHandler = () => {
      goToPreviousStep();
      return true;
    };

    onRegisterBackHandler(backHandler);
  }, [onRegisterBackHandler, goToPreviousStep]);

  const handleResend = async () => {
    try {
      await resendCode({ verificationId: getValues('verificationId') });
    } catch (error) {
      if (isVerificationRateLimitError(error)) {
        setMaxAttemptsReached(true);
        throw error;
      }

      toast(t('resend_error'), { type: 'error' });
      throw error;
    }
  };

  const validateAndUpdateData = async () => {
    const isOtpValid = await trigger('verificationCode');

    if (isOtpValid) {
      updateFormData('verificationCode', getValues('verificationCode'));
    }

    return isOtpValid;
  };

  const handleContinue = async () => {
    if (isVerifyingRef.current || isVerifying) return;

    isVerifyingRef.current = true;

    try {
      const isValid = await validateAndUpdateData();
      if (!isValid) return;

      await verifyCode({ verificationId: getValues('verificationId'), code: getValues('verificationCode') });
      invalidateMyAccountVerificationQueries(queryClient);
      goToNextStep();
    } catch (error) {
      resolveVerificationMutationFailure(error, {
        onRateLimit: () => setMaxAttemptsReached(true),
        onTechnical: () => setShowTechnicalFailure(true),
        onBusiness: () => {
          const message = t('error');

          if (getSiteConfig().MY_ACCOUNT_SETTINGS.showsWrongOtpAsToast) {
            toast(message, { type: 'error' });
          } else {
            setError('verificationCode', { message });
          }
        },
      });
    } finally {
      isVerifyingRef.current = false;
    }
  };

  if (showTechnicalFailure) {
    return (
      <div className="flex flex-col h-full" data-testid="verification-step">
        <SettingsVerificationTechnicalFailure
          action={
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={() => onVerificationFailureDone?.()}
              data-testid="verification-technical-failure-done"
              data-track-name="Done"
            >
              {tTechnicalFailure('done')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="verification-step">
      <div className="md:mt-6">
        <NumericCode size="extra-extra-large" className="mb-5" data-testid="numeric-code-icon" />
        <h4 id="verification-step-title" className="mb-8 text-pretty break-words" data-testid="verification-step-title">
          {t.rich('title', {
            number: chunks => (
              <span className="break-all" data-track-privacy="Mask">
                {chunks}
              </span>
            ),
            mobileNumber: MOBILE_NUMBER,
          })}
        </h4>

        <FormField
          control={control}
          name="verificationCode"
          render={({ field, fieldState }) => (
            <FormItem className="flex-1">
              <FormControl>
                <OtpInput
                  id="otp-input"
                  aria-labelledby="verification-step-title"
                  storageKey={otpResendStorageKey}
                  startCooldownOnMount
                  maxAttemptsReachedOverride={maxAttemptsReached}
                  onOtpChange={field.onChange}
                  onResend={handleResend}
                  helpMessageType={fieldState.error ? 'error' : undefined}
                  helpMessage={formState.errors.verificationCode?.message}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="text-center mt-auto md:mt-8" data-track-container="Actions">
        <ContinueButton onClick={handleContinue} disabled={!isVerificationCodeComplete} loading={isVerifying} data-track-name="Continue">
          {t('update')}
        </ContinueButton>
      </div>
    </div>
  );
};

export { VerificationStep };
